import { useState } from 'react';
import { Callout, Field, Modal, TextInput, explainFailure } from '@affdice/portal-kit';

import { t } from '../messages.ts';
import type { LifecycleMode } from './entity-actions.ts';

/**
 * Delete, restore or purge one operator, brand or affiliate profile — one
 * dialog for all three kinds, because the acts are the same and a second copy
 * is where the wording drifts.
 *
 * The caller says which act (`mode`) and how to run it; this asks for the
 * reason every act needs and, for an operator or a brand, the exact name
 * typed back, so a misclick costs a re-type and not a record. The server
 * refuses an early purge and names the date it becomes possible; that refusal
 * is shown as it comes. "Delete permanently" (`purge-now`) is the super
 * admin's alone and is only offered to them.
 */
export function LifecycleDialog({
  mode,
  kind,
  name,
  needsName,
  run,
  onClose,
  onDone,
}: {
  mode: LifecycleMode;
  kind: 'operator' | 'brand' | 'affiliate';
  name: string;
  /** Operators and brands ask for their name typed back; an affiliate profile does not. */
  needsName: boolean;
  run: (body: { reason: string; confirm_name?: string }) => Promise<unknown>;
  onClose: () => void;
  /** After the act has gone through. */
  onDone: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const asksName = needsName && mode !== 'restore';
  const ready = reason.trim().length > 0 && (!asksName || typed === name);
  const destructive = mode !== 'restore';

  const title =
    mode === 'delete' ? t('ent.life.title.delete', { name })
      : mode === 'restore' ? t('ent.life.title.restore', { name })
        : mode === 'purge' ? t('ent.life.title.purge', { name })
          : t('ent.life.title.purgeNow', { name });
  const note =
    mode === 'delete' ? t('ent.life.note.delete')
      : mode === 'restore' ? t('ent.life.note.restore')
        : mode === 'purge' ? t('ent.life.note.purge')
          : t('ent.life.note.purgeNow');
  const effect =
    kind === 'operator' ? t('ent.life.effect.operator')
      : kind === 'brand' ? t('ent.life.effect.brand')
        : t('ent.life.effect.affiliate');
  const action =
    mode === 'delete' ? t('ent.life.action.delete')
      : mode === 'restore' ? t('ent.life.action.restore')
        : mode === 'purge' ? t('ent.life.action.purge')
          : t('ent.life.action.purgeNow');
  const busyLabel =
    mode === 'delete' ? t('ent.life.busy.delete')
      : mode === 'restore' ? t('ent.life.busy.restore')
        : t('ent.life.busy.purge');

  return (
    <Modal title={title} onClose={() => { if (!busy) onClose(); }}>
      <div className="form">
        {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
        <Callout tone={mode === 'purge' || mode === 'purge-now' ? 'error' : mode === 'delete' ? 'caution' : 'note'}>
          <span className="stack">
            <span>{note}</span>
            {mode === 'delete' ? <span>{effect}</span> : null}
          </span>
        </Callout>
        <Field label={t('common.reason')}>
          <TextInput value={reason} onChange={setReason} />
        </Field>
        {asksName ? (
          <Field label={t('ent.life.confirmName')} hint={t('ent.life.confirmNameHint', { name })}>
            <TextInput value={typed} onChange={setTyped} placeholder={name} />
          </Field>
        ) : null}
        <div className="form-actions">
          <button type="button" className="copy" disabled={busy} onClick={onClose}>{t('common.cancel')}</button>
          <button
            type="button"
            className={destructive ? 'danger' : 'primary'}
            disabled={busy || !ready}
            onClick={() => {
              setBusy(true);
              setFailure(null);
              run({ reason: reason.trim(), ...(asksName ? { confirm_name: typed } : {}) })
                .then(onDone)
                .catch((e: unknown) => setFailure(e))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? busyLabel : action}
          </button>
        </div>
      </div>
    </Modal>
  );
}
